/**
 * Indexes Service - Fetches index metadata for tables
 */

import type { DatabaseEngineId } from '@/core/engines';
import { executeQueryV2 } from './queryExecutor';
import { createExplorerExecutionId } from './explorerQueryUtils';
import { getQueries } from './queries';

export interface IndexInfo {
  name: string;
  columns: string[];
  isUnique: boolean;
  indexType: string;
}

function toStr(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value);
}

function toBool(value: unknown): boolean {
  if (value === true || value === 1 || value === '1' || value === 'true') return true;
  return false;
}

function toNum(value: unknown): number {
  if (value === null || value === undefined) return 0;
  const n = Number(value);
  return isNaN(n) ? 0 : n;
}

function getRowValue(row: Record<string, unknown>, keys: string[]): unknown {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined) return value;
  }
  return undefined;
}

export function mapIndexInfoRows(rows: Record<string, unknown>[]): IndexInfo[] {
  // Group by index name, sort columns by key_ordinal
  const grouped = new Map<string, { isUnique: boolean; indexType: string; columns: { name: string; ordinal: number }[] }>();

  for (const row of rows) {
    const name = toStr(getRowValue(row, ['index_name']));
    const columnName = toStr(getRowValue(row, ['column_name']));
    const isUnique = toBool(getRowValue(row, ['is_unique']));
    const indexType = toStr(getRowValue(row, ['index_type']));
    const ordinal = toNum(getRowValue(row, ['key_ordinal']));

    if (!name || !columnName) continue;

    if (!grouped.has(name)) {
      grouped.set(name, { isUnique, indexType, columns: [] });
    }

    const entry = grouped.get(name)!;

    // Avoid duplicate columns
    if (!entry.columns.some((c) => c.name === columnName)) {
      entry.columns.push({ name: columnName, ordinal });
    }
  }

  return Array.from(grouped.entries()).map(([name, { isUnique, indexType, columns }]) => ({
    name,
    columns: columns.sort((a, b) => a.ordinal - b.ordinal).map((c) => c.name),
    isUnique,
    indexType,
  }));
}

/**
 * Format an index's display name in SSMS style
 * e.g. "IX_ProductName (Name, Category) [UNIQUE, NONCLUSTERED]"
 */
export function formatIndexDisplayName(index: IndexInfo): string {
  const colNames = index.columns.join(', ');
  const flags: string[] = [];
  if (index.isUnique) flags.push('UNIQUE');
  if (index.indexType) flags.push(index.indexType);

  const suffix = flags.length > 0 ? ` [${flags.join(', ')}]` : '';
  return `${index.name} (${colNames})${suffix}`;
}

export async function getIndexes(
  connectionId: string,
  database: string,
  schema: string,
  tableName: string,
  engine?: DatabaseEngineId | string
): Promise<{ indexes: IndexInfo[]; error?: string }> {
  if (engine === 'sqlite') {
    return getIndexesSqlite(connectionId, database, tableName);
  }

  const queries = getQueries(engine);

  try {
    const result = await executeQueryV2({
      connectionId,
      sql: queries.tableIndexes(schema, tableName),
      database,
      executionId: createExplorerExecutionId(connectionId),
    });

    if (!result.success) {
      const errorMsg = result.error?.message || 'Unknown error';
      console.error(`[IndexesService] Error getting indexes: ${errorMsg}`);
      return { indexes: [], error: errorMsg };
    }

    const rows = result.resultSets?.[0]?.rows || [];
    const indexes = mapIndexInfoRows(rows);

    return { indexes };
  } catch (error) {
    console.error('[IndexesService] Error in getIndexes:', error);
    return {
      indexes: [],
      error: error instanceof Error ? error.message : 'Unknown error getting indexes',
    };
  }
}

// --- SQLite-specific index extraction (PRAGMAs) ---

export function mapSqliteIndexes(
  indexListRows: Record<string, unknown>[],
  indexInfoMap: Map<string, Record<string, unknown>[]>
): IndexInfo[] {
  return indexListRows
    .filter((r) => String(r.origin) !== 'pk')
    .map((r) => {
      const name = String(r.name);
      const isUnique = Number(r.unique) === 1;
      const cols = (indexInfoMap.get(name) || [])
        .sort((a, b) => Number(a.seqno) - Number(b.seqno))
        .map((ir) => String(ir.name))
        .filter(Boolean);
      return { name, columns: cols, isUnique, indexType: '' };
    });
}

async function getIndexesSqlite(
  connectionId: string,
  database: string,
  tableName: string
): Promise<{ indexes: IndexInfo[]; error?: string }> {
  const esc = tableName.replace(/'/g, "''");
  const exec = (sql: string) =>
    executeQueryV2({
      connectionId,
      sql,
      database,
      executionId: createExplorerExecutionId(connectionId),
    });

  try {
    const listResult = await exec(`PRAGMA index_list('${esc}')`);
    if (!listResult.success) {
      return { indexes: [], error: listResult.error?.message || 'Unknown error' };
    }

    const indexRows = (listResult.resultSets?.[0]?.rows || []).filter(
      (r) => String(r.origin) !== 'pk'
    );

    const indexInfoMap = new Map<string, Record<string, unknown>[]>();
    await Promise.all(
      indexRows.map(async (r) => {
        const name = String(r.name);
        const res = await exec(
          `PRAGMA index_info('${name.replace(/'/g, "''")}')`
        );
        if (res.success) {
          indexInfoMap.set(name, res.resultSets?.[0]?.rows || []);
        }
      })
    );

    return { indexes: mapSqliteIndexes(indexRows, indexInfoMap) };
  } catch (error) {
    console.error('[IndexesService] Error in getIndexesSqlite:', error);
    return {
      indexes: [],
      error: error instanceof Error ? error.message : 'Unknown error getting indexes',
    };
  }
}
